import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  RotateCcw,
  TrendingUp,
  Eye,
  EyeOff,
  Download,
  Share2,
  Sparkles,
  Zap,
  CheckCircle2,
  XCircle,
  Activity,
  ArrowRight,
  Hash
} from 'lucide-react';
import "@/App.css";

// Derivative calculation with detailed steps
const calculateDerivative = (expression, variable, order = 1) => {
  const steps = [];
  let current = expression.trim();
  
  // Simple polynomial derivative calculator
  const parsePolynomial = (expr) => {
    // Match terms like 5x^3, -2x^2, 3x, 7
    const termRegex = /([+-]?\d*\.?\d*)\*?([a-z])\^?(\d+)?/gi;
    const constantRegex = /^([+-]?\d+\.?\d*)$/;
    
    const terms = [];
    let match;
    
    // Remove spaces
    expr = expr.replace(/\s/g, '');
    
    while ((match = termRegex.exec(expr)) !== null) {
      const coef = match[1] === '' || match[1] === '+' ? 1 : match[1] === '-' ? -1 : parseFloat(match[1]);
      const variable = match[2];
      const power = match[3] ? parseInt(match[3]) : 1;
      terms.push({ coef, variable, power });
    }
    
    // Check for constant term
    const constMatch = expr.match(constantRegex);
    if (constMatch && terms.length === 0) {
      terms.push({ coef: parseFloat(constMatch[1]), variable: null, power: 0 });
    }
    
    return terms;
  };
  
  steps.push(`Given function: f(${variable}) = ${current}`);
  steps.push(`Finding the ${order === 1 ? 'first' : order === 2 ? 'second' : order + 'th'} derivative with respect to ${variable}`);
  
  let derivedTerms = parsePolynomial(current);
  
  for (let i = 0; i < order; i++) {
    steps.push(`\nApplying derivative rules (order ${i + 1}):`);
    
    const newTerms = [];
    derivedTerms.forEach(term => {
      if (term.variable === null || term.power === 0) {
        steps.push(`  • Derivative of constant ${term.coef} = 0`);
      } else if (term.power === 1) {
        steps.push(`  • d/d${variable}(${term.coef}${variable}) = ${term.coef} (Power rule: d/dx(x) = 1)`);
        newTerms.push({ coef: term.coef, variable: null, power: 0 });
      } else {
        const newCoef = term.coef * term.power;
        const newPower = term.power - 1;
        steps.push(`  • d/d${variable}(${term.coef}${variable}^${term.power}) = ${newCoef}${variable}^${newPower} (Power rule)`);
        newTerms.push({ coef: newCoef, variable: term.variable, power: newPower });
      }
    });
    
    derivedTerms = newTerms.filter(t => !(t.variable === null && t.power === 0 && t.coef === 0));
  }
  
  // Construct result
  let result = '';
  if (derivedTerms.length === 0) {
    result = '0';
  } else {
    derivedTerms.forEach((term, idx) => {
      if (term.variable === null || term.power === 0) {
        result += (idx === 0 ? '' : term.coef >= 0 ? ' + ' : ' - ') + Math.abs(term.coef);
      } else if (term.power === 1) {
        const coefStr = Math.abs(term.coef) === 1 ? '' : Math.abs(term.coef);
        result += (idx === 0 ? (term.coef < 0 ? '-' : '') : term.coef >= 0 ? ' + ' : ' - ') + coefStr + term.variable;
      } else {
        const coefStr = Math.abs(term.coef) === 1 ? '' : Math.abs(term.coef);
        result += (idx === 0 ? (term.coef < 0 ? '-' : '') : term.coef >= 0 ? ' + ' : ' - ') + coefStr + term.variable + '^' + term.power;
      }
    });
  }
  
  steps.push(`\nFinal result: f${"'".repeat(order)}(${variable}) = ${result}`);
  
  return { result, steps, order };
};

// Common derivative rules
const derivativeRules = [
  {
    name: "Power Rule",
    formula: "d/dx(x^n) = nx^(n-1)",
    example: "d/dx(x^3) = 3x^2",
    description: "Multiply by the power, then reduce the power by 1"
  },
  {
    name: "Constant Rule",
    formula: "d/dx(c) = 0",
    example: "d/dx(5) = 0",
    description: "The derivative of any constant is zero"
  },
  {
    name: "Constant Multiple Rule",
    formula: "d/dx(cf(x)) = c·f'(x)",
    example: "d/dx(3x^2) = 3·2x = 6x",
    description: "Constants can be factored out of the derivative"
  },
  {
    name: "Sum Rule",
    formula: "d/dx(f + g) = f' + g'",
    example: "d/dx(x^2 + x) = 2x + 1",
    description: "The derivative of a sum is the sum of derivatives"
  },
  {
    name: "Product Rule",
    formula: "d/dx(f·g) = f'g + fg'",
    example: "d/dx(x·sin(x)) = sin(x) + x·cos(x)",
    description: "Derivative of first times second plus first times derivative of second"
  },
  {
    name: "Quotient Rule",
    formula: "d/dx(f/g) = (f'g - fg')/g^2",
    example: "d/dx(x/sin(x)) = (sin(x) - x·cos(x))/sin^2(x)",
    description: "Low d-high minus high d-low, all over low squared"
  },
  {
    name: "Chain Rule",
    formula: "d/dx(f(g(x))) = f'(g(x))·g'(x)",
    example: "d/dx(sin(x^2)) = cos(x^2)·2x",
    description: "Derivative of outer function times derivative of inner function"
  },
  {
    name: "Exponential Rule",
    formula: "d/dx(e^x) = e^x",
    example: "d/dx(e^(2x)) = 2e^(2x)",
    description: "The exponential function is its own derivative"
  },
  {
    name: "Logarithm Rule",
    formula: "d/dx(ln(x)) = 1/x",
    example: "d/dx(ln(3x)) = 1/x",
    description: "The derivative of natural log is one over x"
  },
  {
    name: "Trigonometric Rules",
    formula: "d/dx(sin(x)) = cos(x), d/dx(cos(x)) = -sin(x)",
    example: "d/dx(tan(x)) = sec^2(x)",
    description: "Each trig function has its specific derivative"
  }
];

// Example problems
const exampleProblems = [
  { expr: "3x^2 + 2x + 1", desc: "Simple polynomial" },
  { expr: "5x^4 - 3x^3 + x", desc: "Higher degree polynomial" },
  { expr: "x^3 + 2x^2 - 5x + 7", desc: "Mixed polynomial" },
  { expr: "2x^5 + 4x^3 - x", desc: "Odd powers" },
  { expr: "x^2", desc: "Basic quadratic" },
  { expr: "10x", desc: "Linear function" },
];

// Copy Button Component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title={`Copy ${label}`}>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

// Property Card Component
const PropertyCard = ({ label, value, color, icon: Icon, formula }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`rounded-xl p-4 ${color} border`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <CopyButton text={value} label={label} />
    </div>
    <div className="font-mono text-lg font-bold text-foreground mb-1">
      {value}
    </div>
    {formula && (
      <div className="text-xs text-muted-foreground font-medium mt-2 font-mono bg-background/50 px-2 py-1 rounded">
        {formula}
      </div>
    )}
  </motion.div>
);

function DerivativeCalculator() {
  const [expression, setExpression] = useState("3x^2 + 2x + 1");
  const [variable, setVariable] = useState("x");
  const [order, setOrder] = useState(1);
  const [showRules, setShowRules] = useState(true);
  const [showSteps, setShowSteps] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  
  const result = useMemo(() => {
    try {
      return calculateDerivative(expression, variable, order);
    } catch (e) {
      return null;
    }
  }, [expression, variable, order]);
  
  const handleReset = useCallback(() => {
    setExpression("3x^2 + 2x + 1");
    setVariable("x");
    setOrder(1);
  }, []);
  
  const loadExample = (expr) => {
    setExpression(expr);
  };
  
  const handleExport = () => {
    if (!result) return;
    const data = {
      expression,
      variable,
      order,
      result: result.result,
      steps: result.steps,
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `derivative-${Date.now()}.json`;
    a.click();
  };
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Derivative Calculator - Step-by-Step Solutions with All Rules</title>
        <meta name="description" content="Advanced derivative calculator with detailed step-by-step solutions. Apply power rule, chain rule, product rule, quotient rule and more." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <Activity className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Derivative Calculator</h1>
              <p className="text-muted-foreground mt-1">Compute derivatives with step-by-step rule application</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!result} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <Card className="p-3 bg-blue-500/5 border-blue-500/20">
              <div className="text-xs text-muted-foreground mb-1">Order</div>
              <div className="text-lg font-bold text-blue-600">{result.order}{result.order === 1 ? 'st' : result.order === 2 ? 'nd' : result.order === 3 ? 'rd' : 'th'}</div>
            </Card>
            <Card className="p-3 bg-cyan-500/5 border-cyan-500/20">
              <div className="text-xs text-muted-foreground mb-1">Variable</div>
              <div className="text-lg font-bold text-cyan-600">{variable}</div>
            </Card>
            <Card className="p-3 bg-teal-500/5 border-teal-500/20">
              <div className="text-xs text-muted-foreground mb-1">Steps</div>
              <div className="text-lg font-bold text-teal-600">{result.steps.length}</div>
            </Card>
            <Card className="p-3 bg-sky-500/5 border-sky-500/20">
              <div className="text-xs text-muted-foreground mb-1">Status</div>
              <div className="text-sm font-bold text-sky-600">Computed</div>
            </Card>
          </motion.div>
        )}
      </motion.div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto">
          <TabsTrigger value="calculator" className="py-3">Calculator</TabsTrigger>
          <TabsTrigger value="rules" className="py-3">Derivative Rules</TabsTrigger>
          <TabsTrigger value="examples" className="py-3">Examples</TabsTrigger>
          <TabsTrigger value="guide" className="py-3">Complete Guide</TabsTrigger>
        </TabsList>
        
        {/* Calculator Tab */}
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start space-y-6"
            >
              <Card className="rounded-2xl p-6 border-2 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-foreground text-lg">Input Function</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                    onClick={handleReset}
                    title="Reset All"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Function f({variable})</Label>
                    <Input
                      type="text"
                      value={expression}
                      onChange={(e) => setExpression(e.target.value)}
                      className="h-12 text-lg font-mono"
                      placeholder="e.g., 3x^2 + 2x + 1"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Use ^ for powers (e.g., x^2, x^3)
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Variable</Label>
                    <Input
                      type="text"
                      value={variable}
                      onChange={(e) => setVariable(e.target.value)}
                      className="h-12 text-lg font-mono"
                      maxLength={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Derivative Order</Label>
                    <Input
                      type="number"
                      value={order}
                      onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
                      className="h-12 text-lg font-mono"
                      min="1"
                      max="10"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      1 = first derivative (f'), 2 = second derivative (f''), etc.
                    </p>
                  </div>
                </div>
                
                {/* Validation Status */}
                <div className="mt-6">
                  {result ? (
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Valid Expression ✓</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-destructive block">Invalid Expression</span>
                        <span className="text-xs text-destructive/80">Check your function syntax</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              
              {/* Quick Examples */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Quick Examples
                </h3>
                <div className="space-y-2">
                  {exampleProblems.map((ex, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="w-full justify-start text-left h-auto py-3"
                      onClick={() => loadExample(ex.expr)}
                    >
                      <div className="flex-1">
                        <div className="font-mono text-sm">{ex.expr}</div>
                        <div className="text-xs text-muted-foreground">{ex.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ))}
                </div>
              </Card>
            </motion.div>
            
            {/* Center/Right Column - Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Result Display */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Result
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="p-6 bg-background/60 rounded-xl border-2">
                      <div className="text-sm text-muted-foreground mb-2">Original Function:</div>
                      <div className="font-mono text-xl font-bold text-foreground mb-4">
                        f({variable}) = {expression}
                      </div>
                      
                      <div className="text-sm text-muted-foreground mb-2">
                        {order === 1 ? 'First' : order === 2 ? 'Second' : order + 'th'} Derivative:
                      </div>
                      <div className="font-mono text-2xl font-bold text-green-600">
                        f{"'".repeat(order)}({variable}) = {result.result}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <CopyButton text={result.result} label="derivative" />
                      <Button variant="outline" size="sm" className="gap-2">
                        <Download className="w-3 h-3" />
                        Save Result
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Step-by-Step Solution */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Step-by-Step Solution
                      <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSteps(!showSteps)}
                      className="gap-2"
                    >
                      {showSteps ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      <span className="hidden sm:inline">{showSteps ? 'Hide' : 'Show'}</span>
                    </Button>
                  </div>
                  
                  <AnimatePresence>
                    {showSteps && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3"
                      >
                        {result.steps.map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="flex gap-3 p-3 bg-muted/30 rounded-lg border"
                          >
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-foreground font-mono whitespace-pre-line">{step}</p>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              )}
              
              {/* Additional Information */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                  <Info className="w-5 h-5 text-purple-600" />
                  Understanding Derivatives
                </h2>
                
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">What is a Derivative?</h3>
                    <p className="leading-relaxed">
                      The derivative measures the rate of change of a function. It tells you how fast the function is changing at any given point. 
                      Geometrically, it represents the slope of the tangent line to the function's curve.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Notation</h3>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>f'(x) - Lagrange's notation</li>
                      <li>df/dx - Leibniz's notation</li>
                      <li>Df(x) - Euler's notation</li>
                      <li>ḟ(x) - Newton's notation</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Higher Order Derivatives</h3>
                    <p className="leading-relaxed">
                      Second derivatives (f'') measure the rate of change of the rate of change (acceleration). 
                      Third derivatives and beyond have applications in physics, economics, and engineering.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Derivative Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              Complete Derivative Rules Reference
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {derivativeRules.map((rule, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-5 rounded-xl border-2 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-lg text-foreground">{rule.name}</h3>
                    <Badge variant="secondary" className="text-xs">{idx + 1}</Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-background/60 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Formula:</div>
                      <code className="font-mono text-sm font-bold">{rule.formula}</code>
                    </div>
                    
                    <div className="bg-background/60 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Example:</div>
                      <code className="font-mono text-sm">{rule.example}</code>
                    </div>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {rule.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
          
          {/* Advanced Rules */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              Advanced Derivative Formulas
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Inverse Trigonometric Functions</h3>
                <div className="space-y-1 text-sm font-mono">
                  <div>d/dx(arcsin(x)) = 1/√(1-x²)</div>
                  <div>d/dx(arccos(x)) = -1/√(1-x²)</div>
                  <div>d/dx(arctan(x)) = 1/(1+x²)</div>
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Hyperbolic Functions</h3>
                <div className="space-y-1 text-sm font-mono">
                  <div>d/dx(sinh(x)) = cosh(x)</div>
                  <div>d/dx(cosh(x)) = sinh(x)</div>
                  <div>d/dx(tanh(x)) = sech²(x)</div>
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Parametric Derivatives</h3>
                <div className="text-sm font-mono">
                  dy/dx = (dy/dt)/(dx/dt)
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Implicit Differentiation</h3>
                <div className="text-sm">
                  For equations like x² + y² = r², differentiate both sides with respect to x, treating y as a function of x.
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
        
        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Worked Examples
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="ex-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 1: Simple Polynomial</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find d/dx(3x² + 5x - 7)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Apply power rule to 3x²: d/dx(3x²) = 3·2x = 6x</li>
                        <li>Apply power rule to 5x: d/dx(5x) = 5</li>
                        <li>Derivative of constant -7: d/dx(-7) = 0</li>
                        <li>Combine results: 6x + 5</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">f'(x) = 6x + 5</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 2: Higher Degree Polynomial</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find d/dx(2x⁴ - 3x³ + x² - 8)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>d/dx(2x⁴) = 2·4x³ = 8x³</li>
                        <li>d/dx(-3x³) = -3·3x² = -9x²</li>
                        <li>d/dx(x²) = 2x</li>
                        <li>d/dx(-8) = 0</li>
                        <li>Combine: 8x³ - 9x² + 2x</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">f'(x) = 8x³ - 9x² + 2x</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 3: Second Derivative</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find d²/dx²(x⁴ + 3x²)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>First derivative: f'(x) = 4x³ + 6x</li>
                        <li>Second derivative of 4x³: d/dx(4x³) = 12x²</li>
                        <li>Second derivative of 6x: d/dx(6x) = 6</li>
                        <li>Combine: 12x² + 6</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">f''(x) = 12x² + 6</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 4: Multiple Terms with Different Powers</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find d/dx(5x⁵ - 2x⁴ + 7x² - 3x + 10)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>d/dx(5x⁵) = 25x⁴</li>
                        <li>d/dx(-2x⁴) = -8x³</li>
                        <li>d/dx(7x²) = 14x</li>
                        <li>d/dx(-3x) = -3</li>
                        <li>d/dx(10) = 0</li>
                        <li>Combine all terms: 25x⁴ - 8x³ + 14x - 3</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">f'(x) = 25x⁴ - 8x³ + 14x - 3</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Practice Problems */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Hash className="w-5 h-5 text-orange-600" />
              Practice Problems
            </h2>
            
            <div className="space-y-3">
              {[
                "Find d/dx(x³ + 4x² - 2x + 1)",
                "Find d²/dx²(x⁴ - 6x²)",
                "Find d/dx(10x⁵ - 5x³ + 2x)",
                "Find the third derivative of x⁶",
                "Find d/dx(x⁷ - 3x⁴ + x² - 9)"
              ].map((problem, idx) => (
                <div key={idx} className="p-4 bg-background/60 rounded-lg border hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="font-mono text-sm">{problem}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
        
        {/* Complete Guide Tab */}
        <TabsContent value="guide" className="space-y-6">
          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What is the geometric meaning of a derivative?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Geometrically, the derivative at a point represents the slope of the tangent line to the curve at that point. 
                      It tells you how steep the curve is and in which direction it's going.
                    </p>
                    <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                      <strong className="text-blue-600">Key Points:</strong>
                      <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                        <li>Positive derivative = function is increasing</li>
                        <li>Negative derivative = function is decreasing</li>
                        <li>Zero derivative = function has a horizontal tangent (critical point)</li>
                        <li>Undefined derivative = function has a vertical tangent or discontinuity</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Why do we need higher order derivatives?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Higher order derivatives provide information about how the rate of change itself is changing. They have important applications across many fields.
                    </p>
                    <div className="space-y-3">
                      <div className="bg-background/60 p-3 rounded-lg border">
                        <strong>Second Derivative (f''):</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          Measures concavity and acceleration. Positive f'' means the function is concave up (curving upward), 
                          negative f'' means concave down. In physics, it represents acceleration.
                        </p>
                      </div>
                      <div className="bg-background/60 p-3 rounded-lg border">
                        <strong>Third Derivative (f'''):</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          Called "jerk" in physics - the rate of change of acceleration. Important in engineering for smooth motion control.
                        </p>
                      </div>
                      <div className="bg-background/60 p-3 rounded-lg border">
                        <strong>Fourth+ Derivatives:</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          Used in advanced physics, Taylor series approximations, and solving differential equations.
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">How do I know which rule to apply?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Choosing the right derivative rule depends on the structure of your function. Here's a decision guide:
                    </p>
                    <div className="space-y-2">
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Single term with power:</strong>
                        <span className="text-sm text-muted-foreground ml-2">Use Power Rule</span>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Sum of multiple terms:</strong>
                        <span className="text-sm text-muted-foreground ml-2">Use Sum Rule (differentiate each term separately)</span>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Two functions multiplied:</strong>
                        <span className="text-sm text-muted-foreground ml-2">Use Product Rule</span>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">One function divided by another:</strong>
                        <span className="text-sm text-muted-foreground ml-2">Use Quotient Rule</span>
                      </div>
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Function inside another function:</strong>
                        <span className="text-sm text-muted-foreground ml-2">Use Chain Rule</span>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What are critical points and why are they important?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Critical points are points where the derivative is zero or undefined. They're crucial for finding maximum and minimum values of functions.
                    </p>
                    <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                      <strong className="text-purple-600">Finding and Classifying Critical Points:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 text-sm">
                        <li>Find f&apos;(x) and set it equal to zero</li>
                        <li>Solve for x to get critical points</li>
                        <li>Use second derivative test: f&apos;&apos;(x) {`>`} 0 = local minimum, f&apos;&apos;(x) {`<`} 0 = local maximum</li>
                        <li>If f&apos;&apos;(x) = 0, use higher derivatives or first derivative test</li>
                      </ol>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What is the relationship between derivatives and integrals?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      Derivatives and integrals are inverse operations, connected by the Fundamental Theorem of Calculus.
                    </p>
                    <div className="space-y-3">
                      <div className="bg-background/60 p-4 rounded-lg border">
                        <strong>Part 1:</strong>
                        <p className="text-sm mt-2">If F(x) is the integral of f(x), then F'(x) = f(x)</p>
                        <code className="text-xs font-mono block mt-2">d/dx[∫f(x)dx] = f(x)</code>
                      </div>
                      <div className="bg-background/60 p-4 rounded-lg border">
                        <strong>Part 2:</strong>
                        <p className="text-sm mt-2">If f is continuous on [a,b], then:</p>
                        <code className="text-xs font-mono block mt-2">∫[a to b] f(x)dx = F(b) - F(a)</code>
                        <p className="text-xs text-muted-foreground mt-2">where F is any antiderivative of f</p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Real-World Applications */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              Real-World Applications of Derivatives
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Physics & Motion
                </h3>
                <p className="text-xs text-muted-foreground">
                  Velocity is the derivative of position, acceleration is the derivative of velocity. 
                  Used to analyze projectile motion, orbital mechanics, and particle physics.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Activity className="w-4 h-4 text-blue-600" />
                  Economics & Finance
                </h3>
                <p className="text-xs text-muted-foreground">
                  Marginal cost and marginal revenue are derivatives. Used for optimization of profit, 
                  portfolio analysis, and option pricing models like Black-Scholes.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  Engineering
                </h3>
                <p className="text-xs text-muted-foreground">
                  Stress-strain relationships, electrical circuit analysis, fluid dynamics, 
                  control systems, and optimization of structures and processes.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Info className="w-4 h-4 text-orange-600" />
                  Biology & Medicine
                </h3>
                <p className="text-xs text-muted-foreground">
                  Population growth models, spread of diseases (epidemiology), 
                  pharmacokinetics (drug concentration over time), and enzyme kinetics.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-cyan-600" />
                  Machine Learning
                </h3>
                <p className="text-xs text-muted-foreground">
                  Gradient descent optimization, backpropagation in neural networks, 
                  and finding optimal parameters for models.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Lightbulb className="w-4 h-4 text-yellow-600" />
                  Computer Graphics
                </h3>
                <p className="text-xs text-muted-foreground">
                  Ray tracing, surface normal calculation for lighting, animation curves, 
                  and spline interpolation for smooth motion.
                </p>
              </div>
            </div>
          </Card>
          
          {/* Historical Context */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-amber-500/5 to-orange-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-amber-600" />
              Historical Development
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">17th Century - The Birth of Calculus</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Isaac Newton and Gottfried Leibniz independently developed calculus. Newton called derivatives "fluxions" 
                  and used them to describe motion and change in physics. Leibniz developed the notation we still use today (dy/dx).
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">18th-19th Century - Rigorous Foundations</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Mathematicians like Cauchy, Weierstrass, and Riemann developed the rigorous epsilon-delta definition of limits, 
                  placing calculus on solid logical foundations and enabling modern mathematical analysis.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">20th Century - Modern Applications</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Calculus became essential for quantum mechanics, relativity, computer science, and economics. 
                  Automatic differentiation algorithms now power modern AI and machine learning systems.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default DerivativeCalculator;